import {isNil} from 'lodash';
import throttle from 'lodash/throttle';
import {RefObject, useEffect, useMemo} from 'react';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {events} from '../constant/textViewerSagaEvents';
import {
  setCurrentChapterId,
  setCurrentHtmlInfo,
  setCurrentPage,
  setCurrentPageCount,
} from '../state/textViewerSlice';
import {FrameSize} from '../type/FrameSize.interface';
import {calculatePreviewTextViewerCurrentPage} from '../util/calculatePreviewTextViewerCurrentPage';
import {isElementInView} from '../util/isElementInView';

/**
 * 스크롤 위치에 따라 현재 챕터 및 페이지 정보를 갱신해주는 컴포넌트입니다.
 */
export function PreviewTextViewerPositionTracker({
  productId,
  size,
  wrapperRef,
}: {
  productId: number;
  size: FrameSize;
  wrapperRef?: RefObject<HTMLDivElement>;
}) {
  const {
    currentPage,
    currentChapterId,
    chapterPageCountList,
    isContentLoaded,
    isDrawing,
    isPageCalculating,
    htmlInfoList,
  } = useTypedSelector(state => ({
    currentPage: state.textViewer.viewData[productId]?.currentPage,
    currentChapterId: state.textViewer.viewData[productId]?.currentChapterId,
    chapterPageCountList:
      state.textViewer.viewData[productId]?.chapterPageCountList,
    isContentLoaded: state.textViewer.viewData[productId]?.isContentLoaded,
    isDrawing: state.textViewer.viewData[productId]?.isDrawing,
    isPageCalculating: state.textViewer.viewData[productId]?.isPageCalculating,
    htmlInfoList: state.textViewer.viewData[productId]?.htmlInfoList,
  }));
  const dispatch = useTypedDispatch();

  const trackPageChangeWhenScroll = useMemo(
    () =>
      throttle(() => {
        if (
          !isContentLoaded ||
          isDrawing ||
          isPageCalculating ||
          !currentPage ||
          !chapterPageCountList ||
          !wrapperRef?.current
        ) {
          return;
        }

        const contentWrapper = wrapperRef.current;
        const chapterListContainer = contentWrapper.firstChild;

        if (!chapterListContainer) {
          return;
        }

        let currentChapterContainer: HTMLDivElement | null = null;
        let newCurrentChapterId: number | null = null;

        for (const [
          index,
          childNode,
        ] of chapterListContainer.childNodes.entries() as IterableIterator<
          [number, HTMLDivElement]
        >) {
          // 뷰 내에 존재하는 챕터들 중 가장 상단에 위치하는 챕터를 기준으로 계산합니다.
          if (isElementInView(childNode, -60)) {
            newCurrentChapterId = index;
            currentChapterContainer = childNode;
          }
        }

        if (!currentChapterContainer || isNil(newCurrentChapterId)) {
          return;
        }

        const containerRect = currentChapterContainer.getBoundingClientRect();
        const wrapperStartY = window.scrollY + Math.floor(containerRect.y);
        const wrapperBottomY = wrapperStartY + containerRect.height;
        const pageHeight = size.height;

        const newCurrentPage = calculatePreviewTextViewerCurrentPage({
          pageHeight,
          wrapperStartY,
          wrapperBottomY,
          currentScrollY: window.scrollY,
          currentPageCount: chapterPageCountList[newCurrentChapterId],
        });

        if (currentChapterId !== newCurrentChapterId) {
          dispatch(
            setCurrentChapterId({
              productId,
              chapterId: newCurrentChapterId,
              setIsDrawing: false,
            })
          );
          dispatch(
            setCurrentPageCount({
              productId,
              pageCount: chapterPageCountList[newCurrentChapterId],
            })
          );
          if (htmlInfoList?.[newCurrentChapterId]) {
            dispatch(
              setCurrentHtmlInfo({
                productId,
                htmlInfo: htmlInfoList[newCurrentChapterId],
              })
            );
          }
        }
        if (currentPage !== newCurrentPage) {
          dispatch(setCurrentPage({productId, page: newCurrentPage}));
        }

        dispatch(
          events.updateScrollViewerCurrentRateRequired({
            productId,
            wrapperStartY,
            wrapperBottomY,
          })
        );
      }, 100),
    [
      chapterPageCountList,
      currentChapterId,
      currentPage,
      dispatch,
      htmlInfoList,
      isContentLoaded,
      isDrawing,
      isPageCalculating,
      productId,
      size.height,
      wrapperRef,
    ]
  );

  useEffect(() => {
    window.addEventListener('scroll', trackPageChangeWhenScroll);

    return () => {
      window.removeEventListener('scroll', trackPageChangeWhenScroll);
    };
  }, [trackPageChangeWhenScroll]);

  return null;
}
